import {
    Button,
    createStyles,
    Grid,
    GridList,
    GridListTile,
    IconButton,
    TextField,
    withStyles,
    WithStyles,
} from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import _ from 'lodash';
import { inject } from 'mobx-react';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { RouteComponentProps } from 'react-router';
import IDataTableManager from 'src/common/IDataTableManager';
import {
    DefaultAffinityCluster,
    IAffinityCluster,
    IAssessment,
    IObservation,
} from 'src/Definitions';
import { AffinityClusterDto, ObservationDto } from 'src/generated_client';
import { IStores } from 'src/Stores';
import InjectedAffinityClusterDialog from './AffinityClusterDialog';
import InjectedDeleteAffinityClusterDialog from './DeleteAffinityClusterDialog';
import ObservationCard from './ObservationCard';

const styles = createStyles({
    addIcon: {
        fontSize: '3vh',
        fontWeight: 'bold',
        height: '50%',
        hyphens: 'auto',
        paddingTop: '5%',
        textAlign: 'center',
    },
    affinityClusterCard: {
        alignItems: 'center',
        backgroundColor: 'white',
        boxShadow: '0px 0px 5px grey',
        cursor: 'pointer',
        display: 'flex',
        height: '150px',
        margin: '5px',
        padding: '10px',
    },
    clusterName: {
        fontSize: '3vh',
        fontWeight: 'bold',
        height: '90%',
        hyphens: 'auto',
        textAlign: 'center',
    },
    clusterNameCreate: {
        fontSize: '3vh',
        fontWeight: 'bold',
        height: '50%',
        hyphens: 'auto',
        textAlign: 'center',
    },
    clusterObservationCount: {
        height: '10%',
        paddingRight: '5px',
        textAlign: 'right',
    },
    clusterObservationSave: {
        height: '30%',
        paddingRight: '5px',
        textAlign: 'right',
    },
    clusterTextField: {
        fontSize: '3vh',
        fontWeight: 'bold',
        height: '70%',
        hyphens: 'auto',
        textAlign: 'center',
    },
    container: {
        color: 'grey',
        padding: '50px',
    },
    observationCard: {
        color: 'black',
        margin: '10px',
        padding: '10px',
    },
    observationType: {
        textAlign: 'right',
    },
    observationsContainer: {
        backgroundColor: 'white',
        borderRadius: '30px',
        height: '600px',
        overflowY: 'auto',
        padding: '10px',
    },
});

interface IMatchParams {
    assessmentId: string;
}

interface IAffinityClustersProps
    extends WithStyles<typeof styles>,
        RouteComponentProps<IMatchParams> {
    assessment: IAssessment;
    assessmentId: string;
    affinityCluster?: IAffinityCluster;
    affinityClusters?: IAffinityCluster[];
    dataTableManager?: IDataTableManager<IAffinityCluster>;
    disableAffinityCluster?: (affinityCluster: IAffinityCluster) => Promise<void>;
    isAffinityClusterLoading?: boolean;
    isDeleteModalOpen?: boolean;
    isModalOpen?: boolean;
    isObservationLoading?: boolean;
    observations?: IObservation[];
    openDeleteClusterDialog?: (affinityCluster: IAffinityCluster) => void;
    openDialogWithAffinityCluster?: (affinityCluster: IAffinityCluster) => void;
    recoverAffinityCluster?: (affinityCluster: IAffinityCluster) => void;
    getAllAffinityClusters?: (assessmentId: string) => Promise<void>;
    getAssessmentById?: (assessmentId: string) => void;
    getNextObservationsPage?: (
        assessmentId: string,
        pageSize: number,
        unattachedToClusterOnly?: boolean
    ) => Promise<void>;
    getObservations?: (
        assessmentId: string,
        unattachedToClusterOnly?: boolean,
        pageNumber?: number,
        pageSize?: number
    ) => Promise<void>;
    saveAffinityCluster?: (affinityCluster: IAffinityCluster) => Promise<void>;
    saveObservation?: (observation: IObservation, shouldResetUIState: boolean) => Promise<void>;
}

class AffinityClusters extends React.Component<IAffinityClustersProps> {
    public state = {
        assessmentId: this.props.match.params.assessmentId,
        newAffinityClusters: [] as IAffinityCluster[],
        uncategorizedObservations: this.props.observations,
    };

    public componentDidMount() {
        this.props.getAssessmentById!(this.state.assessmentId);
        this.props.getAllAffinityClusters!(this.state.assessmentId);
        this.props.getObservations!(this.state.assessmentId, true, 1, 15).then(() => {
            this.updateState();
        });
    }

    public onDragEnd = (result: any) => {
        if (result.destination && this.props.observations) {
            const selectedCluster = this.props.affinityClusters!.find(
                (x) => x.id === result.destination.droppableId
            ) as IAffinityCluster;
            const selectedObservation = this.props.observations!.find(
                (x) => x.id === result.draggableId
            ) as IObservation;
            if (selectedCluster) {
                selectedCluster.observations!.push(({
                    ...selectedObservation,
                } as unknown) as ObservationDto);
                selectedObservation.affinityClusterId = selectedCluster.id;

                this.props.saveObservation!(selectedObservation, false).then(() => {
                    this.updateState();
                });
            }
        }
    };

    public createNew = () => () => {
        const updatedAffinityClusters = this.state.newAffinityClusters;
        const newAffinityCluster = (new AffinityClusterDto({
            ...DefaultAffinityCluster(),
            assessment: undefined,
            assessmentId: this.state.assessmentId,
            isActive: true,
            name: '',
        }) as unknown) as IAffinityCluster;
        newAffinityCluster.isNew = true;
        updatedAffinityClusters.push(newAffinityCluster);
        this.setState({ newAffinityClusters: updatedAffinityClusters });
    };

    public setName = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedAffinityClusters = this.state.newAffinityClusters || [];
        if (updatedAffinityClusters[index]) {
            updatedAffinityClusters[index].name = event.target.value;
            this.setState({ newAffinityClusters: updatedAffinityClusters });
        }
        event.preventDefault();
        event.stopPropagation();
    };

    public saveAffinityCluster = (affinityCluster: IAffinityCluster) => (
        event: React.MouseEvent
    ) => {
        event.preventDefault();
        event.stopPropagation();
        this.props.saveAffinityCluster!(affinityCluster).then(() => {
            this.props.getAllAffinityClusters!(this.state.assessmentId);
            _.remove(
                this.state.newAffinityClusters,
                (cluster) => affinityCluster.id === cluster.id
            );
        });
    };

    public disableAffinityCluster = (affinityCluster: IAffinityCluster) => () => {
        this.props.disableAffinityCluster!(affinityCluster).then(() => {
            this.props.getAllAffinityClusters!(this.state.assessmentId);
        });
    };

    public render() {
        const affinityClusterDialog = this.props.isModalOpen && (
            <InjectedAffinityClusterDialog
                affinityCluster={this.props.affinityCluster}
                assessmentId={this.state.assessmentId}
                undo={this.undo}
            />
        );
        const deleteAffinityClusterDialog = this.props.isDeleteModalOpen && (
            <InjectedDeleteAffinityClusterDialog
                affinityCluster={this.props.affinityCluster}
                updateState={this.updateState}
            />
        );
        const { affinityClusters, assessment } = this.props;
        const { newAffinityClusters } = this.state;
        return (
            <Grid container direction="column" className={this.props.classes.container}>
                <Grid item>
                    <h1>{assessment ? assessment.name : ''}</h1>
                </Grid>
                <DragDropContext onDragEnd={this.onDragEnd}>
                    <Grid container direction="row" spacing={16}>
                        <Grid item xs={9}>
                            <GridList cols={3} spacing={12}>
                                {affinityClusters &&
                                    affinityClusters.map((affinityCluster) => (
                                        <GridListTile key={affinityCluster.id}>
                                            {affinityCluster.isActive &&
                                                this.renderAffinityCluster(affinityCluster)}
                                        </GridListTile>
                                    ))}
                                {newAffinityClusters &&
                                    newAffinityClusters.map((affinityCluster, index) => (
                                        <GridListTile key={affinityCluster.id}>
                                            <div className={this.props.classes.affinityClusterCard}>
                                                <Grid
                                                    style={{ height: '100%' }}
                                                    container
                                                    direction="column"
                                                    spacing={8}
                                                >
                                                    <TextField
                                                        className={
                                                            this.props.classes.clusterTextField
                                                        }
                                                        multiline
                                                        variant="outlined"
                                                        value={affinityCluster.name}
                                                        label="Category Name"
                                                        onChange={this.setName(index)}
                                                        required
                                                    />
                                                    <div
                                                        className={
                                                            this.props.classes
                                                                .clusterObservationSave
                                                        }
                                                    >
                                                        <Button
                                                            variant="outlined"
                                                            onClick={this.saveAffinityCluster(
                                                                affinityCluster
                                                            )}
                                                            disabled={affinityCluster.name === ''}
                                                        >
                                                            Save
                                                        </Button>
                                                    </div>
                                                </Grid>
                                            </div>
                                        </GridListTile>
                                    ))}
                                <Grid key="createNew">
                                    <div
                                        className={this.props.classes.affinityClusterCard}
                                        onClick={this.createNew()}
                                    >
                                        <Grid
                                            style={{ height: '100%' }}
                                            container
                                            direction="column"
                                            spacing={8}
                                        >
                                            <div className={this.props.classes.addIcon}>
                                                <AddCircleOutlineIcon fontSize="large" />
                                            </div>
                                            <div className={this.props.classes.clusterNameCreate}>
                                                Create New
                                            </div>
                                        </Grid>
                                    </div>
                                </Grid>
                            </GridList>
                        </Grid>
                        <Grid item xs={3}>
                            <Grid
                                direction="column"
                                container
                                className={this.props.classes.observationsContainer}
                                onScroll={this.onObservationScroll}
                            >
                                {this.renderUncategorizedObservations()}
                            </Grid>
                        </Grid>
                    </Grid>
                    {affinityClusterDialog}
                    {deleteAffinityClusterDialog}
                </DragDropContext>
            </Grid>
        );
    }

    private renderAffinityCluster(affinityCluster: IAffinityCluster) {
        return (
            <Droppable droppableId={affinityCluster.id}>
                {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        <div className={this.props.classes.affinityClusterCard}>
                            <Grid
                                style={{ height: '100%' }}
                                container
                                direction="column"
                                alignItems="flex-end"
                            >
                                <Grid style={{ maxHeight: '10%' }} item>
                                    <IconButton
                                        style={{ padding: '4px', margin: '-12px -12px 0 0' }}
                                        aria-label="delete"
                                        onClick={this.openDeleteDialog(affinityCluster)}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Grid>
                                <Grid
                                    style={{ height: '90%' }}
                                    item
                                    container
                                    direction="column"
                                    justify="space-between"
                                    onClick={this.openDialog(affinityCluster)}
                                >
                                    <Grid item>
                                        <div className={this.props.classes.clusterName}>
                                            {affinityCluster.name}
                                        </div>
                                    </Grid>
                                    <Grid item>
                                        <Grid
                                            container
                                            direction="row"
                                            justify="space-between"
                                            alignItems="flex-end"
                                        >
                                            <Grid item />
                                            <Grid item>
                                                <div
                                                    className={
                                                        this.props.classes.clusterObservationCount
                                                    }
                                                >
                                                    {affinityCluster.observations!.length}
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </div>
                    </div>
                )}
            </Droppable>
        );
    }

    private renderUncategorizedObservations() {
        let observations: IObservation[] = [];
        if (this.state.uncategorizedObservations) {
            observations = [...this.state.uncategorizedObservations];
        }

        return (
            <Droppable droppableId={'observations'}>
                {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        <Grid item>
                            {observations.map((observation: IObservation, index: number) => {
                                return this.renderObservation(observation, index);
                            })}
                        </Grid>
                    </div>
                )}
            </Droppable>
        );
    }

    private renderObservation(observation: IObservation, index: number) {
        return (
            <Draggable
                isDragDisabled={
                    this.props.isObservationLoading || this.props.isAffinityClusterLoading
                }
                key={observation.id}
                draggableId={observation.id}
                index={index}
            >
                {(provided, snapshot) => {
                    const style: React.CSSProperties = {
                        ...provided.draggableProps.style,
                        backgroundColor: 'white',
                    };
                    if (snapshot.isDragging) {
                        style.border = '1px solid rgba(224, 224, 224, 1)';
                    }
                    return (
                        <div
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                            style={style}
                        >
                            <ObservationCard
                                classes={this.props.classes}
                                observation={observation}
                                key={observation.id}
                            />
                        </div>
                    );
                }}
            </Draggable>
        );
    }

    private updateState = (shouldReloadObservations: boolean = false) => {
        if (shouldReloadObservations) {
            this.props.getObservations!(this.state.assessmentId, true, 1, 15).then(() => {
                this.updateState(); // Recursive call here to call the setState.
            });
        }
        if (this.props.observations) {
            this.setState({
                uncategorizedObservations: this.props.observations.filter(
                    (x) => x.affinityClusterId === undefined || x.affinityClusterId === ''
                ),
            });
        }
    };

    private onObservationScroll = (scrollEvent: React.SyntheticEvent) => {
        const reachedBottom =
            scrollEvent.currentTarget.scrollHeight - scrollEvent.currentTarget.scrollTop <=
            scrollEvent.currentTarget.clientHeight;
        if (reachedBottom) {
            this.props.getNextObservationsPage!(this.state.assessmentId, 15, true).then(() => {
                this.updateState();
            });
        }
    };

    public openDialog = (affinityCluster: IAffinityCluster) => () => {
        this.props.openDialogWithAffinityCluster!(affinityCluster);
    };

    public openDeleteDialog = (affinityCluster: IAffinityCluster) => () => {
        this.props.openDeleteClusterDialog!(affinityCluster);
    };

    public undo = (observation: IObservation) => {
        observation.affinityClusterId = '';
        this.props.saveObservation!(observation, false).then(() => {
            this.props.observations!.unshift(observation);
            this.updateState();
        });
    };
}

const InjectedAffinityClusters = inject<
    IStores,
    IAffinityClustersProps,
    Partial<IAffinityClustersProps>,
    any
>((stores: IStores) => ({
    affinityCluster: stores.affinityClusters.selectedAffinityCluster,
    affinityClusters: stores.affinityClusters.affinityClusters,
    assessment: stores.assessments.assessment,
    dataTableManager: stores.affinityClusters.dataTableStore,
    disableAffinityCluster: stores.affinityClusters.disableAffinityCluster,
    getAllAffinityClusters: stores.affinityClusters.getAllAffinityClusters,
    getAssessmentById: stores.assessments.getAssessmentById,
    getNextObservationsPage: stores.observations.getNextObservationsByAssessmentPage,
    getObservations: stores.observations.getObservationsByAssessmentId,
    isAffinityClusterLoading: stores.affinityClusters.isLoading,
    isDeleteModalOpen: stores.affinityClusters.isDeleteModalOpen,
    isModalOpen: stores.affinityClusters.isModalOpen,
    isObservationLoading: stores.observations.isLoading,
    observations: stores.observations.observations,
    openDeleteClusterDialog: stores.affinityClusters.openDeleteDialog,
    openDialogWithAffinityCluster: stores.affinityClusters.openDialogWithAffinityCluster,
    recoverAffinityCluster: stores.affinityClusters.recoverAffinityCluster,
    saveAffinityCluster: stores.affinityClusters.saveAffinityCluster,
    saveObservation: stores.observations.saveObservation,
}))(withStyles(styles)(AffinityClusters));

export default InjectedAffinityClusters;



// WEBPACK FOOTER //
// ./src/affinityClusters/AffinityClusters.tsx